import { useCitadel } from "@/hooks/use-citadel";
import type { ContactTab } from "@/sites/contacts/contact/tabs";

/**
 * Which tabs of the contact detail page this caller has no rule for.
 *
 * A contact is four subjects, and two of them are the commercial record: the projects a client has
 * and the services it fulfils. An ordinary employee holds neither, so those two panels would answer
 * 403 and toast an error on a page they were never meant to see. They are dropped instead.
 *
 * The list is a hook rather than a computation at each site because two things have to agree about
 * it: `Contact` renders the tabs, and `contactTabPath` turns a tab's name into the index `Tabs`
 * addresses panels by — an index over the panels that are actually rendered. Whoever links to a tab
 * passes this list, so a caller who cannot see the projects tab still lands on the members tab.
 *
 * The same three-state guard the `Can` component documents applies: a tab disappears only on a
 * definitive denial, so rules still loading keep it and let the server have the last word.
 */
export function useHiddenContactTabs(): ContactTab[] {
	const { citadel } = useCitadel();
	if (!citadel) return [];
	const hidden: ContactTab[] = [];
	if (citadel.cannot("read", "ContactProjects")) hidden.push("projects");
	if (citadel.cannot("read", "ContactServices")) hidden.push("services");
	return hidden;
}
